import type { ComponentType } from 'preact'
import { useEffect } from 'preact/hooks'
import type { Store } from '../net/store'
import { useStore } from '../ui/useStore'
import styles from './daily.module.css'
import type { DailyView } from './dailyView'

/** The store side `DailyBox` needs: the view and a re-read when it opens. */
export type DailySource = Store<DailyView | null> & { refresh(): Promise<void> }

/**
 * "Daily tasks" (T2.5, D-053 component `DailyBox`): today's tasks with their progress, ✓ when
 * done, the reward, and "New tasks in N h" (UTC midnight). Pure view of a `DailyView`.
 */
export function DailyBoxView({ view }: { view: DailyView }) {
  return (
    <section class={styles.box} aria-label="Daily tasks" data-testid="daily-box">
      <header class={styles.head}>
        <h2 class={styles.title}>Daily tasks</h2>
        <span class={styles.reset}>New tasks in {view.hoursToReset} h</span>
      </header>
      <ul class={styles.list}>
        {view.tasks.map((t) => (
          <li key={t.id} class={`${styles.task} ${t.done ? styles.done : ''}`} data-task={t.id}>
            <span class={styles.check} aria-hidden="true">
              {t.done ? '✓' : ''}
            </span>
            <span class={styles.text}>{t.text}</span>
            <span class={styles.count}>
              {t.progress}/{t.target}
            </span>
            <span class={styles.reward}>+{t.coins} coins</span>
            <span
              class={styles.bar}
              role="progressbar"
              aria-label={t.text}
              aria-valuemin={0}
              aria-valuemax={t.target}
              aria-valuenow={t.progress}
            >
              <span class={styles.fill} style={{ width: `${(100 * t.progress) / t.target}%` }} />
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** No view yet (offline, or before the first read): a short note instead of an empty card. */
export function DailyBoxEmpty() {
  return (
    <section class={styles.box} aria-label="Daily tasks" data-testid="daily-box-empty">
      <header class={styles.head}>
        <h2 class={styles.title}>Daily tasks</h2>
      </header>
      <p class={styles.reset}>Daily tasks show up when you are online.</p>
    </section>
  )
}

/** The no-props component the session exposes; a short note while the view is null. */
export function makeDailyBox(source: DailySource): ComponentType {
  return function DailyBox() {
    const view = useStore(source)
    useEffect(() => {
      void source.refresh()
    }, [])
    return view ? <DailyBoxView view={view} /> : <DailyBoxEmpty />
  }
}
